import apiRoutes from '@constants/apiRoutes';
import baseApi from '@services/api';
import {
  GenerateInvestorReportPayload,
  GameListResponse,
  InvestorReportListingData,
  GenerateInvestorReportResponse,
  InvestorReportByIdPayload,
  SlideListData,
  SlidesListPayload,
  SlideDetailsData,
  SlideDetailsPayload,
  InvestorReportGuest,
  ReqInvestorReportSuggestionDto,
  ReqUpdateSuggestionDto,
  ResInvestorReportSuggestionDto,
  SuggestionDto,
  SuggestionSlidePayload,
  InvestorReportShareItem,
  ShareReportPayload,
  RevokeReportSharePayload,
  GameContactPoc,
  SharedGame,
} from './types/investor-report';
import { ApiResponse } from './types/common';
import { SlideData } from '../pages/Dashboard/components/InvestorReport/InvestorReportEdit/components/Reel/components/Section/components/Slide/types';
import { ReportHistory } from './types/investor-report/reportHistory';
import { InvestorReportExportResponse } from './types/investor-report/investorReportExportResponse';
import { GameStatus } from '@constants/constants';

export enum ReportGenerationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  // This is not a valid status, it is used to indicate that an error occurred while getting the status of the report generation
  API_ERROR = 'API_ERROR',
}

const investorReport = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInvestorReports: builder.query<
      ApiResponse<InvestorReportListingData>,
      {
        pageNumber: number;
        pageSize: number;
        search?: string;
        selectedStatusFilter: GameStatus[];
        hasPendingSuggestions?: boolean;
      }
    >({
      query: ({ pageNumber, pageSize, search, selectedStatusFilter, hasPendingSuggestions }) => {
        const queryParams = [];
        if (pageNumber || pageNumber === 0) {
          queryParams.push(`pageNumber=${pageNumber}`);
        }
        if (pageSize) {
          queryParams.push(`pageSize=${pageSize}`);
        }
        if (search) {
          queryParams.push(`search=${search}`);
        }
        if (selectedStatusFilter && selectedStatusFilter?.length >= 1) {
          queryParams.push(
            `gameStatus=${selectedStatusFilter.length === 1 ? selectedStatusFilter[0] : selectedStatusFilter.join(',')}`,
          );
        }
        if (hasPendingSuggestions !== undefined) {
          queryParams.push(`hasPendingSuggestions=${hasPendingSuggestions}`);
        }

        return apiRoutes.investorReport(queryParams.join('&'));
      },
    }),

    getInvestorReportById: builder.query<
      ApiResponse<GenerateInvestorReportResponse>,
      InvestorReportByIdPayload
    >({
      query: ({ reportId }) => apiRoutes.investorReportReportById(reportId),
    }),

    generateInvestorReport: builder.mutation<
      ApiResponse<GenerateInvestorReportResponse>,
      GenerateInvestorReportPayload
    >({
      query: (payload) => ({
        url: apiRoutes.generateInvestorReport,
        method: 'POST',
        body: payload,
      }),
    }),

    deleteInvestorReport: builder.mutation<void, { exportId: string }>({
      query: ({ exportId }) => ({
        url: apiRoutes.deleteInvestorReport(exportId),
        method: 'DELETE',
      }),
    }),

    refreshInvestorReport: builder.mutation<
      ApiResponse<GenerateInvestorReportResponse>,
      { reportId: string; months?: number }
    >({
      query: ({ reportId, months }) => ({
        url: apiRoutes.refreshInvestorReport(reportId),
        method: 'POST',
        body: typeof months === 'number' ? { cohortCutoff: months } : undefined,
      }),
    }),

    getInvestorReportGamesList: builder.query<ApiResponse<GameListResponse>, void>({
      query: () => apiRoutes.investorReportGamesList,
    }),

    getAdminInvestorReportGamesList: builder.query<ApiResponse<GameListResponse>, void>({
      query: () => apiRoutes.adminInvestorReportGamesList,
    }),

    getSlides: builder.query<ApiResponse<SlideListData[]>, SlidesListPayload>({
      query: ({ reportId }) => apiRoutes.slides(reportId),
    }),

    getSlideById: builder.query<ApiResponse<SlideDetailsData>, SlideDetailsPayload>({
      query: ({ reportId, slideId }) => apiRoutes.slideById(reportId, slideId),
    }),

    updateSlideData: builder.mutation<
      void,
      { payload: SlideData; reportId: string; slideId: string }
    >({
      query: ({
        payload,
        reportId,
        slideId,
      }: {
        payload: SlideData;
        reportId: string;
        slideId: string;
      }) => ({
        url: apiRoutes.updateSlideData(reportId, slideId),
        method: 'PUT',
        body: payload,
      }),
    }),

    updateSlideDataForClientsAllowed: builder.mutation<
      void,
      { payload: SlideData; reportId: string; slideId: string }
    >({
      query: ({ payload, reportId, slideId }) => ({
        url: apiRoutes.updateSlideDataClientAllowed(reportId, slideId),
        method: 'PUT',
        body: payload,
      }),
    }),

    uploadGameLogo: builder.mutation<ApiResponse<string>, { gameId: string; file: File }>({
      query: ({ gameId, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: apiRoutes.uploadGameLogo(gameId),
          method: 'POST',
          body: formData,
        };
      },
    }),

    getGameLogo: builder.query<ApiResponse<string>, { gameId: string; fileName: string }>({
      query: ({ gameId, fileName }) =>
        `${apiRoutes.getGameLogo}?gameId=${gameId}&fileName=${encodeURIComponent(fileName)}`,
    }),

    getInvestorReportPdfById: builder.query<ApiResponse<string>, { reportId: string }>({
      query: ({ reportId }) => apiRoutes.investorReportPdfById(reportId),
    }),

    getInvestorReportHistory: builder.query<ApiResponse<ReportHistory[]>, { reportId: string }>({
      query: ({ reportId }) => apiRoutes.getInvestorReportHistory(reportId),
    }),

    getInvestorReportPdfDownloadUrl: builder.query<ApiResponse<string>, { exportId: string }>({
      query: ({ exportId }) => apiRoutes.getInvestorReportPdfDownloadUrl(exportId),
    }),

    uploadInvestorReportChartImage: builder.mutation<
      ApiResponse<string>,
      { reportId: string; slideId: string; file: File }
    >({
      query: ({ reportId, slideId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: apiRoutes.uploadInvestorReportChartImage(reportId, slideId),
          method: 'POST',
          body: formData,
        };
      },
    }),

    getInvestorReportChartImage: builder.query<
      ApiResponse<string>,
      { reportId: string; slideId: string; fileName: string }
    >({
      query: ({ reportId, slideId, fileName }) =>
        apiRoutes.getInvestorReportChartImage(reportId, slideId, fileName),
    }),

    generateAiCommentary: builder.mutation<
      ApiResponse<string>,
      { reportId: string; slideId: string }
    >({
      query: ({ reportId, slideId }) => ({
        url: apiRoutes.generateAiCommentary(reportId, slideId),
        method: 'POST',
      }),
    }),

    generateAiCompanySummary: builder.mutation<
      ApiResponse<string>,
      { reportId: string; slideId: string }
    >({
      query: ({ reportId, slideId }) => ({
        url: apiRoutes.generateAiCompanySummary(reportId, slideId),
        method: 'POST',
      }),
    }),

    generateInvestorReportPdf: builder.mutation<ApiResponse<string>, { reportId: string }>({
      query: ({ reportId }) => ({
        url: apiRoutes.generateInvestorReportPdf(reportId),
        method: 'POST',
      }),
    }),

    getInvestorReportGenerationStatus: builder.query<
      ApiResponse<InvestorReportExportResponse>,
      { exportId: string; reportId: string }
    >({
      query: ({ exportId, reportId }) =>
        apiRoutes.getInvestorReportGenerationStatus(exportId, reportId),
    }),
    updateSkipSlideConfig: builder.mutation<
      void,
      { reportId: string; slideId: string; skipSlide: boolean }
    >({
      query: ({ reportId, slideId, skipSlide }) => ({
        url: apiRoutes.updateSkipSlideConfig(reportId, slideId),
        method: 'PUT',
        body: { skipSlide },
      }),
    }),

    getInvestorReportGuests: builder.query<ApiResponse<InvestorReportGuest[]>, { shareId: string }>(
      {
        query: ({ shareId }) => apiRoutes.getInvestorReportGuests(shareId),
      },
    ),

    getInvestorReportShareLink: builder.query<ApiResponse<string>, { gameId: string }>({
      query: ({ gameId }) =>
        `${apiRoutes.getShareLinkForInvestorReport('INVESTOR_REPORT_EXT', gameId)}`,
    }),

    /** @deprecated */
    inviteToInvestorReport: builder.mutation<
      ApiResponse<void>,
      {
        email: string;
        gameId: string;
      }
    >({
      query: ({ email, gameId }) => ({
        url: apiRoutes.inviteToEmailForSharing,
        method: 'POST',
        body: {
          userEmail: email,
          gameId,
          submoduleType: 'INVESTOR_REPORT_EXT',
          invitationType: 'SUBMODULE_SHARE',
        },
      }),
    }),

    /** @deprecated */
    revokeInvestorReportAccess: builder.mutation<
      ApiResponse<void>,
      { shareId: string; email: string }
    >({
      query: ({ shareId, email }) => ({
        url: apiRoutes.revokeInvestorReportAccess(shareId, email),
        method: 'DELETE',
      }),
    }),

    /** @deprecated Use recordShareActivity instead */
    updateInvestorReportViewedStatus: builder.mutation<ApiResponse<void>, { shareId: string }>({
      query: ({ shareId }) => ({
        url: apiRoutes.updateInvestorReportViewedStatus(shareId),
        method: 'PUT',
      }),
    }),

    generateInvestorReportByGameId: builder.mutation<
      ApiResponse<GenerateInvestorReportResponse>,
      { gameId: string; websiteUrl?: string; linkedinUrl?: string }
    >({
      query: ({ gameId, websiteUrl, linkedinUrl }) => ({
        url: apiRoutes.generateInvestorReportByGameId,
        method: 'POST',
        body: {
          gameId,
          websiteUrl,
          linkedinUrl,
        },
      }),
    }),

    getInvestorReportSampleReport: builder.query<ApiResponse<string>, void>({
      query: () => apiRoutes.getInvestorReportSampleReport,
    }),

    getSuggestions: builder.query<ApiResponse<SuggestionDto[]>, SuggestionSlidePayload>({
      query: ({ reportId, slideId }) => apiRoutes.investorReportSuggestions(reportId, slideId),
    }),

    createOrUpdateSuggestions: builder.mutation<
      ApiResponse<ResInvestorReportSuggestionDto[]>,
      SuggestionSlidePayload & { suggestions: ReqInvestorReportSuggestionDto[] }
    >({
      query: ({ reportId, slideId, suggestions }) => ({
        url: apiRoutes.investorReportSuggestions(reportId, slideId),
        method: 'POST',
        body: suggestions,
      }),
    }),

    reviewSuggestions: builder.mutation<
      ApiResponse<ResInvestorReportSuggestionDto[]>,
      SuggestionSlidePayload & { payload: ReqUpdateSuggestionDto }
    >({
      query: ({ reportId, slideId, payload }) => ({
        url: apiRoutes.investorReportSuggestionsReview(reportId, slideId),
        method: 'POST',
        body: payload,
      }),
    }),

    // ---- Share & Activity ----

    recordShareActivity: builder.mutation<ApiResponse<null>, { shareId: string }>({
      query: ({ shareId }) => ({
        url: apiRoutes.recordShareActivity(shareId),
        method: 'POST',
        body: {
          activityType: 'VIEWED',
        },
      }),
    }),

    getContactDetails: builder.query<ApiResponse<GameContactPoc[]>, { reportId: string }>({
      query: ({ reportId }) => apiRoutes.investorReportContactDetails(reportId),
    }),

    // ---- Share & Activity END ----

    // ---- Investor Report Sharing ----

    getSharedGames: builder.query<ApiResponse<SharedGame[]>, void>({
      query: () => apiRoutes.investorReportSharedGames,
    }),

    shareInvestorReport: builder.mutation<
      ApiResponse<InvestorReportShareItem[]>,
      { reportId: string; payload: ShareReportPayload }
    >({
      query: ({ reportId, payload }) => ({
        url: apiRoutes.shareInvestorReport(reportId),
        method: 'POST',
        body: payload,
      }),
    }),

    getInvestorReportShareList: builder.query<
      ApiResponse<InvestorReportShareItem[]>,
      { reportId: string }
    >({
      query: ({ reportId }) => apiRoutes.shareInvestorReport(reportId),
    }),

    revokeInvestorReportShare: builder.mutation<
      ApiResponse<null>,
      { reportId: string; payload: RevokeReportSharePayload }
    >({
      query: ({ reportId, payload }) => ({
        url: apiRoutes.revokeInvestorReportShare(reportId, payload.type, payload.id),
        method: 'DELETE',
      }),
    }),

    // ---- Investor Report Sharing END ----
  }),
});

export const {
  useLazyGetInvestorReportsQuery,
  useLazyGetInvestorReportByIdQuery,
  useLazyGetInvestorReportHistoryQuery,
  useGenerateInvestorReportMutation,
  useLazyGetInvestorReportGamesListQuery,
  useLazyGetAdminInvestorReportGamesListQuery,
  useDeleteInvestorReportMutation,
  useRefreshInvestorReportMutation,
  useLazyGetInvestorReportPdfByIdQuery,
  useUploadGameLogoMutation,
  useLazyGetGameLogoQuery,
  useLazyGetSlidesQuery,
  useLazyGetSlideByIdQuery,
  useUpdateSlideDataMutation,
  useUpdateSlideDataForClientsAllowedMutation,
  useLazyGetInvestorReportPdfDownloadUrlQuery,
  useUploadInvestorReportChartImageMutation,
  useLazyGetInvestorReportChartImageQuery,
  useGenerateAiCommentaryMutation,
  useGenerateAiCompanySummaryMutation,
  useGenerateInvestorReportPdfMutation,
  useLazyGetInvestorReportGenerationStatusQuery,
  useUpdateSkipSlideConfigMutation,
  useLazyGetInvestorReportGuestsQuery,
  useLazyGetInvestorReportShareLinkQuery,
  useInviteToInvestorReportMutation,
  useRevokeInvestorReportAccessMutation,
  useUpdateInvestorReportViewedStatusMutation,
  useGenerateInvestorReportByGameIdMutation,
  useLazyGetInvestorReportSampleReportQuery,
  useLazyGetSuggestionsQuery,
  useCreateOrUpdateSuggestionsMutation,
  useReviewSuggestionsMutation,
  // ---- Share & Activity ----
  useRecordShareActivityMutation,
  useLazyGetContactDetailsQuery,
  // ---- Share & Activity END ----
  // ---- Investor Report Sharing ----
  useLazyGetSharedGamesQuery,
  useShareInvestorReportMutation,
  useLazyGetInvestorReportShareListQuery,
  useRevokeInvestorReportShareMutation,
  // ---- Investor Report Sharing END ----
} = investorReport;
